import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DBContext } from './DBContext';
import TopRootDiv from './TopRootDiv';
import SherDineNav from './SherDineNav';
import SideNav from './SideNav';
import CheckTables from './CheckTables';
import NavBarMobile from './Mobile/NavBar';
import SideNavMob from './SideNavMob';

const EventViewer = () => {
    const navigate = useNavigate();
    useEffect(() => {
        if (localStorage.getItem('USER') !== 'shersoft') {
            navigate('/ViewDashboard');
        }
    }, [navigate]);

    const { logginedDB, sideNavState, sideNavStateFunc } = useContext(DBContext);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchEvents = async (pageNumber = 1) => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiBaseUrl}/api/events/${logginedDB}`, {
                params: { page: pageNumber, limit: 50 },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.data.success) {
                setEvents(response.data.data.events);
                setPagination(response.data.data.pagination);
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching events:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (logginedDB) {
            fetchEvents(page);
        }
    }, [logginedDB, page]);

    const [sideNav, setSideNav] = useState(false);
    const [falseState, setFalseState] = useState(null);
    const handleSideNav = (state) => {
        setSideNav(state);
    };

    const renderEventContent = () => (
        <>
            <CheckTables />

            <div className="w-full flex-1 min-h-0 bg-white mt-4 rounded-lg shadow-md overflow-hidden flex flex-col">
                <div className="p-4 border-b flex justify-between items-center bg-[#f8f9fa]">
                    <h2 className="text-xl font-semibold text-[#464255]">Event Logs</h2>
                    <button
                        onClick={() => fetchEvents(page)}
                        className="bg-[#0008B4] text-white px-4 py-2 rounded-[.313rem] hover:bg-blue-700 transition-colors"
                    >
                        Refresh
                    </button>
                </div>

                <div className="flex-1 overflow-auto customScroll">
                    {loading ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0008B4]"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 p-4">{error}</div>
                    ) : events.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-[20rem] text-gray-400">
                            <span className="text-lg">No logs found for this period.</span>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 bg-[#f8f9fa] shadow-sm z-10">
                                <tr>
                                    <th className="p-3 border-b font-semibold text-[#464255]">Timestamp</th>
                                    <th className="p-3 border-b font-semibold text-[#464255]">Type</th>
                                    <th className="p-3 border-b font-semibold text-[#464255]">Subtype</th>
                                    <th className="p-3 border-b font-semibold text-[#464255]">User</th>
                                    <th className="p-3 border-b font-semibold text-[#464255]">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((event) => (
                                    <tr key={event.id} className="hover:bg-gray-50 border-b last:border-0 relative">
                                        <td className="p-3 text-sm text-gray-600">
                                            {new Date(event.created_at).toLocaleString()}
                                        </td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded text-xs font-medium 
                                                ${event.event_type === 'ERROR' ? 'bg-red-100 text-red-700' :
                                                    event.event_type === 'INFO' ? 'bg-blue-100 text-blue-700' :
                                                        'bg-gray-100 text-gray-700'}`}>
                                                {event.event_type}
                                            </span>
                                        </td>
                                        <td className="p-3 text-sm text-gray-600">{event.event_subtype}</td>
                                        <td className="p-3 text-sm text-gray-600">
                                            <div className="font-medium">{event.user_type}</div>
                                            <div className="text-xs">ID: {event.user_id}</div>
                                        </td>
                                        <td className="p-3 text-sm text-gray-600 max-w-md truncate">
                                            {event.action_details || event.error_message || 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="p-4 border-t flex justify-between items-center bg-[#f8f9fa]">
                    <span className="text-sm text-gray-600">
                        Showing {events.length} of {pagination.total || 0} events
                    </span>
                    <div className="flex gap-2">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
                        >
                            Prev
                        </button>
                        <span className="px-3 py-1 bg-white border rounded">
                            Page {page} of {pagination.totalPages || 1}
                        </span>
                        <button
                            disabled={page === pagination.totalPages}
                            onClick={() => setPage(page + 1)}
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <div className="w-screen h-screen">
            {/* Desktop View */}
            <div className="w-full h-full overflow-hidden hidden sm:flex sm:flex-col">
                <SherDineNav
                    sideNav={(state) => handleSideNav(state)}
                    falseState={falseState}
                />
                <div className="w-full bg-[#f5f5f5] flex gap-3 h-full overflow-hidden">
                    <div className="w-max h-full z-[9999]">
                        <SideNav
                            focus={"EventViewer"}
                            state={sideNav}
                            falseState={(state) => setFalseState(state)}
                        />
                    </div>
                    <div className="w-full h-full flex flex-col p-4 overflow-hidden">
                        <TopRootDiv
                            image="Dashboard"
                            root={['Dashboard', 'Event Viewer']}
                            section="Audit Logs"
                            type="Page"
                        />
                        {renderEventContent()}
                    </div>
                </div>
            </div>

            {/* Mobile View */}
            <div className="w-full h-full flex flex-col sm:!hidden overflow-hidden">
                <div className="w-full h-max sticky top-0">
                    <NavBarMobile title="Event Viewer" />
                </div>
                <div
                    className={`relative w-full h-full bg-[#f5f5f5] overflow-hidden`}
                    onClick={() => sideNavStateFunc && sideNavStateFunc(true)}
                >
                    <div
                        className={`h-full w-max absolute left-0 bg-[#f5f5f5] transition-transform duration-5000 z-[9999] ${
                            sideNavState
                                ? "translate-x-0 ease-out shadow-[5px_5px_100px_rgba(0,0,0,0.4)]"
                                : "-translate-x-full ease-in duration-5000"
                        }`}
                    >
                        <SideNavMob
                            focus="EventViewer"
                            state={sideNav}
                            falseState={(state) => setFalseState(state)}
                        />
                    </div>
                    <div className="w-full h-full flex flex-col p-4 overflow-hidden gap-3">
                        {renderEventContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventViewer;
